import { useState, type FormEvent } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

export function JoinPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Deep-link support: /join?code=ABCDEF pre-fills the code so a
  // shared link from the host is a one-tap path for the guest.
  const [roomCode, setRoomCode] = useState(() => {
    const incoming = searchParams.get("code")
    if (!incoming) return ""
    return incoming.trim().toLocaleUpperCase().slice(0, 6)
  })

  function joinByCode(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const normalized = roomCode.trim().toLocaleUpperCase()

    if (normalized.length !== 6) {
      toast.error("Enter a 6-letter room code")
      return
    }

    // The /r/:code route shows a "Your name" form for guests who
    // don't yet have a session for this room, so we hand off there.
    navigate(`/r/${normalized}`)
  }

  return (
    <main
      id="main"
      className="mx-auto flex min-h-dvh w-full max-w-md flex-col bg-background text-foreground"
    >
      <header className="border-b-2 border-border px-5 py-5">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.35em] text-muted-foreground transition-colors hover:text-foreground"
        >
          ← Back
        </Link>
        <h1 className="mt-3 font-display text-3xl uppercase leading-[0.95] tracking-tight">
          Join a <span className="text-primary">room</span>
        </h1>
        <p className="prose-body mt-2 max-w-[34ch] text-sm text-muted-foreground">
          Ask the host for the 6-letter code, type it below, and you'll
          add your name on the next screen.
        </p>
      </header>

      <section className="flex-1 px-5 py-6">
        <form onSubmit={joinByCode} className="flex flex-col gap-5">
          <label className="flex flex-col gap-2 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
            Room code
            <Input
              value={roomCode}
              onChange={(event) => setRoomCode(event.target.value.toLocaleUpperCase())}
              maxLength={6}
              autoComplete="off"
              autoCapitalize="characters"
              inputMode="text"
              name="room-code"
              placeholder="ABCDEF"
              className="h-16 bg-card text-center font-display text-3xl tracking-[0.3em] text-foreground placeholder:text-muted-foreground/30"
            />
          </label>

          <Button
            type="submit"
            disabled={roomCode.trim().length !== 6}
            className="mt-2 h-14 bg-primary text-primary-foreground hover:bg-primary"
          >
            Join room
          </Button>
        </form>
      </section>
    </main>
  )
}
